import { useEffect, useState } from "react";
import { CalendarPlus, Copy, Download, ExternalLink, Hand, Hash, Loader2, MapPin, Nfc, Pencil, RefreshCw, Trash2, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import {
  deleteMaterialItem,
  getApiErrorMessage,
  getMaterialHistorial,
  getMaterialQrUrl,
  regenerarMaterialEtiqueta,
  updateMaterialItem,
} from "@/lib/api";
import { MaterialEstadoBadge, MaterialFoto } from "@/components/material/MaterialShared";
import ReservaRow from "@/components/material/ReservaRow";

export default function ItemSheet({
  open,
  onOpenChange,
  item,
  focusReservaId,
  me,
  isAdmin,
  usersByEmail,
  refreshKey,
  onReservar,
  onCogerAhora,
  onEditarItem,
  onChanged,
  reservaActions,
}) {
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmRegenerar, setConfirmRegenerar] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    let cancel = false;
    setLoading(true);
    getMaterialHistorial(item.id)
      .then((d) => !cancel && setHistorial(d.reservas || []))
      .catch(() => !cancel && setHistorial([]))
      .finally(() => !cancel && setLoading(false));
    return () => {
      cancel = true;
    };
  }, [open, item, refreshKey]);

  if (!item) return null;

  const ahora = new Date().toISOString();
  const activas = historial.filter((r) => r.estado === "en_uso" || (r.estado === "reservada" && r.fin > ahora)).reverse();
  const pasadas = historial.filter((r) => !activas.includes(r)).slice(0, 15);

  const toggleReparacion = async (v) => {
    setBusy(true);
    try {
      await updateMaterialItem(item.id, { en_reparacion: v });
      toast.success(v ? "Marcado en reparación" : "Vuelve a estar operativo");
      onChanged?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo actualizar"));
    } finally {
      setBusy(false);
    }
  };

  const eliminar = async () => {
    setBusy(true);
    try {
      await deleteMaterialItem(item.id);
      toast.success("Equipo eliminado");
      setConfirmDelete(false);
      onOpenChange(false);
      onChanged?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo eliminar"));
    } finally {
      setBusy(false);
    }
  };

  const copiarUrl = async () => {
    try {
      await navigator.clipboard.writeText(item.etiqueta_url);
      toast.success("URL copiada");
    } catch (_) {
      toast.error("No se pudo copiar");
    }
  };

  const regenerar = async () => {
    setBusy(true);
    try {
      await regenerarMaterialEtiqueta(item.id);
      toast.success("Etiqueta nueva generada. Imprime el QR o regraba el NFC");
      setConfirmRegenerar(false);
      onChanged?.();
    } catch (e) {
      toast.error(getApiErrorMessage(e, "No se pudo regenerar"));
    } finally {
      setBusy(false);
    }
  };

  const rowProps = { item, me, isAdmin, usersByEmail, ...reservaActions };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto overflow-x-hidden p-0 sm:max-w-lg">
          <MaterialFoto item={item} className="aspect-[16/10] w-full shrink-0" iconClassName="h-14 w-14" />
          <div className="flex flex-col gap-5 p-6">
            <SheetHeader className="space-y-2 text-left">
              <MaterialEstadoBadge estado={item.estado} className="w-fit" />
              <SheetTitle className="text-xl">{item.nombre}</SheetTitle>
              <SheetDescription asChild>
                <div className="flex flex-col gap-1 text-sm">
                  {item.ubicacion && (
                    <span className="flex items-center gap-1.5 text-foreground">
                      <MapPin className="h-4 w-4 text-primary" /> Se guarda en <span className="font-medium">{item.ubicacion}</span>
                    </span>
                  )}
                  {item.numero_serie && (
                    <span className="flex items-center gap-1.5">
                      <Hash className="h-4 w-4" /> {item.numero_serie}
                    </span>
                  )}
                </div>
              </SheetDescription>
            </SheetHeader>

            {(item.descripcion || item.notas) && (
              <>
                <Separator className="-mx-6 w-auto" />
                <section className="flex flex-col gap-3">
                  <h3 className={SECCION}>Qué incluye</h3>
                  {item.descripcion && <p className="whitespace-pre-line text-sm leading-relaxed">{item.descripcion}</p>}
                  {item.notas && <p className="whitespace-pre-line rounded-lg bg-muted/60 px-3 py-2 text-sm text-muted-foreground">{item.notas}</p>}
                </section>
              </>
            )}

            <div className="flex gap-2">
              {item.estado === "disponible" && (
                <Button className="flex-1 gap-1.5" onClick={() => onCogerAhora(item)}>
                  <Hand className="h-4 w-4" /> Coger ahora
                </Button>
              )}
              <Button variant="outline" className="flex-1 gap-1.5" onClick={() => onReservar([item.id])} disabled={item.estado === "reparacion"}>
                <CalendarPlus className="h-4 w-4" /> Reservar
              </Button>
            </div>

            <Separator className="-mx-6 w-auto" />

            <section className="flex flex-col gap-2.5">
              <h3 className={SECCION}>Ahora y próximas reservas</h3>
              {loading && !historial.length ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : activas.length ? (
                activas.map((r) => <ReservaRow key={r.id} reserva={r} highlighted={r.id === focusReservaId} {...rowProps} />)
              ) : (
                <p className="text-sm text-muted-foreground">Nadie lo tiene ni lo ha reservado.</p>
              )}
            </section>

            {pasadas.length > 0 && (
              <>
              <Separator className="-mx-6 w-auto" />
              <section className="flex flex-col gap-2.5">
                <h3 className={SECCION}>Historial</h3>
                {pasadas.map((r) => (
                  <ReservaRow key={r.id} reserva={r} highlighted={r.id === focusReservaId} {...rowProps} />
                ))}
              </section>
              </>
            )}

            {isAdmin && item.etiqueta_url && (
              <>
                <Separator className="-mx-6 w-auto" />
                <section className="flex flex-col gap-3">
                  <h3 className={SECCION}>Etiqueta QR / NFC</h3>
                  <div className="flex gap-4">
                    <img
                      src={getMaterialQrUrl(item.id, { version: item.etiqueta_url })}
                      alt="Código QR del equipo"
                      className="h-32 w-32 shrink-0 rounded-lg border bg-white p-1"
                    />
                    <div className="flex min-w-0 flex-col gap-2 text-sm">
                      <p className="text-muted-foreground">
                        Al escanearla cualquiera puede llevárselo o devolverlo, sin iniciar sesión.
                      </p>
                      <code className="truncate rounded-md bg-muted px-2 py-1 text-xs">{item.etiqueta_url}</code>
                      <div className="flex flex-wrap gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 gap-1" onClick={copiarUrl}>
                          <Copy className="h-3.5 w-3.5" /> Copiar URL
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1" asChild>
                          <a href={getMaterialQrUrl(item.id, { formato: "png", descargar: true })}>
                            <Download className="h-3.5 w-3.5" /> PNG
                          </a>
                        </Button>
                        <Button size="sm" variant="outline" className="h-8 gap-1" asChild>
                          <a href={item.etiqueta_url} target="_blank" rel="noreferrer">
                            <ExternalLink className="h-3.5 w-3.5" /> Abrir
                          </a>
                        </Button>
                      </div>
                    </div>
                  </div>
                  <p className="flex items-start gap-2 rounded-lg bg-muted/60 px-3 py-2 text-xs text-muted-foreground">
                    <Nfc className="mt-0.5 h-4 w-4 shrink-0" />
                    Para NFC: graba la URL en la pegatina con una app como NFC Tools (registro de tipo «URL»). Al acercar el móvil se abre la misma página.
                  </p>
                  <Button size="sm" variant="ghost" className="w-fit gap-1.5 text-muted-foreground" onClick={() => setConfirmRegenerar(true)}>
                    <RefreshCw className="h-3.5 w-3.5" /> Generar URL nueva (invalida la etiqueta actual)
                  </Button>
                </section>
              </>
            )}

            {isAdmin && (
              <>
              <Separator className="-mx-6 w-auto" />
              <section className="flex flex-col gap-3 rounded-xl border border-dashed p-4">
                <h3 className={SECCION}>Administración</h3>
                <label className="flex items-center justify-between gap-3 text-sm">
                  <span className="flex items-center gap-2">
                    <Wrench className="h-4 w-4" /> En reparación / no disponible
                  </span>
                  <Switch checked={!!item.en_reparacion} onCheckedChange={toggleReparacion} disabled={busy} />
                </label>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => onEditarItem(item)}>
                    <Pencil className="h-3.5 w-3.5" /> Editar
                  </Button>
                  <Button variant="outline" size="sm" className="gap-1.5 text-destructive hover:text-destructive" onClick={() => setConfirmDelete(true)}>
                    <Trash2 className="h-3.5 w-3.5" /> Eliminar
                  </Button>
                </div>
              </section>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmRegenerar} onOpenChange={setConfirmRegenerar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Generar una URL nueva?</AlertDialogTitle>
            <AlertDialogDescription>
              El QR y el NFC actuales de «{item.nombre}» dejarán de funcionar. Úsalo si la etiqueta se ha perdido o alguien de fuera tiene la URL.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={regenerar} disabled={busy}>
              Generar nueva
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar «{item.nombre}»?</AlertDialogTitle>
            <AlertDialogDescription>Se cancelarán sus reservas pendientes. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={eliminar} disabled={busy} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

const SECCION = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";
